import React, { useMemo } from 'react';
import { TimberItem, SupplierPrice, normalizeMoistureContent } from '../../utils/materialsDatabaseService';
import { X, TrendingUp, TrendingDown, Minus, Package, Users, BarChart3, Layers } from 'lucide-react';
import './TimberReport.css';

interface TimberReportProps {
    isOpen: boolean;
    onClose: () => void;
    items: TimberItem[];
}

const formatCurrency = (val: number) => `$${val.toFixed(2)}`;

function drynessDisplay(m?: string): string {
    const n = normalizeMoistureContent(m);
    if (n === 'green') return 'Green';
    if (n === 'kiln_dried') return 'Kiln dried';
    return '';
}

const TimberReport: React.FC<TimberReportProps> = ({ isOpen, onClose, items }) => {

    // ---- Inventory Summary ----
    const inventorySummary = useMemo(() => {
        const bySpecies = new Map<string, number>();
        const byGrade = new Map<string, number>();
        let totalSupplierEntries = 0;
        const supplierSet = new Set<string>();

        items.forEach(item => {
            bySpecies.set(item.species, (bySpecies.get(item.species) || 0) + 1);
            byGrade.set(item.grade, (byGrade.get(item.grade) || 0) + 1);
            if (item.supplierPricing) {
                totalSupplierEntries += item.supplierPricing.length;
                item.supplierPricing.forEach(sp => supplierSet.add(sp.supplier));
            } else if (item.supplier) {
                supplierSet.add(item.supplier);
                totalSupplierEntries += 1;
            }
        });

        return {
            totalPieces: items.length,
            totalSupplierEntries,
            supplierCount: supplierSet.size,
            bySpecies: Array.from(bySpecies.entries()).sort((a, b) => b[1] - a[1]),
            byGrade: Array.from(byGrade.entries()).sort((a, b) => b[1] - a[1]),
        };
    }, [items]);

    // ---- Supplier Comparison ----
    const supplierComparison = useMemo(() => {
        const supplierStats = new Map<string, {
            totalBdFtPrice: number;
            totalBdFt: number;
            pieceCount: number;
            lowestCount: number;
        }>();

        // Collect all supplier pricing data
        items.forEach(item => {
            if (!item.supplierPricing || item.supplierPricing.length === 0) return;

            // Find cheapest supplier for this item
            let cheapestPrice = Infinity;
            let cheapestSupplier = '';
            item.supplierPricing.forEach(sp => {
                if (sp.costPerBdFt > 0 && sp.costPerBdFt < cheapestPrice) {
                    cheapestPrice = sp.costPerBdFt;
                    cheapestSupplier = sp.supplier;
                }
            });

            item.supplierPricing.forEach(sp => {
                if (!supplierStats.has(sp.supplier)) {
                    supplierStats.set(sp.supplier, {
                        totalBdFtPrice: 0, totalBdFt: 0, pieceCount: 0, lowestCount: 0,
                    });
                }
                const stats = supplierStats.get(sp.supplier)!;
                if (sp.costPerBdFt > 0) {
                    stats.totalBdFtPrice += sp.costPerBdFt * item.boardFeet;
                    stats.totalBdFt += item.boardFeet;
                }
                stats.pieceCount++;
                if (sp.supplier === cheapestSupplier) stats.lowestCount++;
            });
        });

        return Array.from(supplierStats.entries())
            .map(([name, stats]) => ({
                name,
                avgCostPerBdFt: stats.totalBdFt > 0 ? stats.totalBdFtPrice / stats.totalBdFt : 0,
                pieceCount: stats.pieceCount,
                lowestCount: stats.lowestCount,
            }))
            .sort((a, b) => a.avgCostPerBdFt - b.avgCostPerBdFt);
    }, [items]);

    // ---- Species + Grade × Supplier Comparison ----
    const gradeBySupplier = useMemo(() => {
        // Collect all unique suppliers; key rows by species+grade
        const allSuppliers = new Set<string>();
        const rowMap = new Map<string, Map<string, { totalCost: number; totalBdFt: number }>>(); // "species grade" -> supplier -> stats

        items.forEach(item => {
            if (!item.supplierPricing || item.supplierPricing.length === 0) return;
            const dry = drynessDisplay(item.moistureContent);
            const rowKey = dry ? `${item.species} ${item.grade} · ${dry}` : `${item.species} ${item.grade}`;
            if (!rowMap.has(rowKey)) rowMap.set(rowKey, new Map());
            const supplierMap = rowMap.get(rowKey)!;

            item.supplierPricing.forEach(sp => {
                allSuppliers.add(sp.supplier);
                if (!supplierMap.has(sp.supplier)) {
                    supplierMap.set(sp.supplier, { totalCost: 0, totalBdFt: 0 });
                }
                const stats = supplierMap.get(sp.supplier)!;
                if (sp.costPerBdFt > 0) {
                    stats.totalCost += sp.costPerBdFt * item.boardFeet;
                    stats.totalBdFt += item.boardFeet;
                }
            });
        });

        const suppliers = Array.from(allSuppliers).sort();
        const rowKeys = Array.from(rowMap.keys()).sort();

        const rows = rowKeys.map(rowKey => {
            const supplierMap = rowMap.get(rowKey)!;
            const avgBySupplier: { supplier: string; avg: number }[] = suppliers.map(s => {
                const stats = supplierMap.get(s);
                return {
                    supplier: s,
                    avg: stats && stats.totalBdFt > 0 ? stats.totalCost / stats.totalBdFt : 0,
                };
            });
            const cheapest = avgBySupplier.filter(a => a.avg > 0).sort((a, b) => a.avg - b.avg)[0]?.supplier || '';
            return { label: rowKey, avgBySupplier, cheapest };
        });

        return { suppliers, rows };
    }, [items]);

    // ---- Price History (from supplierPricing lastQuoteDate) ----
    const priceHistory = useMemo(() => {
        const entries: {
            date: Date;
            supplier: string;
            species: string;
            dimensions: string;
            costPerBdFt: number;
        }[] = [];

        items.forEach(item => {
            if (!item.supplierPricing) return;
            item.supplierPricing.forEach(sp => {
                if (sp.lastQuoteDate) {
                    entries.push({
                        date: sp.lastQuoteDate,
                        supplier: sp.supplier,
                        species: item.species,
                        dimensions: item.dimensions,
                        costPerBdFt: sp.costPerBdFt,
                    });
                }
            });
        });

        // Group by supplier, show latest quote date per supplier
        const bySupplier = new Map<string, { lastDate: Date; count: number }>();
        entries.forEach(e => {
            const existing = bySupplier.get(e.supplier);
            if (!existing || e.date > existing.lastDate) {
                bySupplier.set(e.supplier, { lastDate: e.date, count: (existing?.count || 0) + 1 });
            } else {
                existing.count++;
            }
        });

        return {
            totalEntries: entries.length,
            bySupplier: Array.from(bySupplier.entries())
                .map(([name, data]) => ({ name, lastDate: data.lastDate, count: data.count }))
                .sort((a, b) => b.lastDate.getTime() - a.lastDate.getTime()),
        };
    }, [items]);

    // ---- Single-Source Risk ----
    const singleSourceCount = useMemo(() => {
        return items.filter(item =>
            !item.supplierPricing || item.supplierPricing.length <= 1
        ).length;
    }, [items]);

    const multiSourceCount = items.length - singleSourceCount;

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="timber-report-modal" onClick={e => e.stopPropagation()}>
                <div className="timber-report-header">
                    <h2>📊 Timber Report</h2>
                    <button className="modal-close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="timber-report-body">
                    {/* Inventory Summary */}
                    <section className="report-section">
                        <h3><Package size={18} /> Inventory Summary</h3>
                        <div className="report-stat-grid">
                            <div className="report-stat-card">
                                <span className="stat-value">{inventorySummary.totalPieces}</span>
                                <span className="stat-label">Unique Pieces</span>
                            </div>
                            <div className="report-stat-card">
                                <span className="stat-value">{inventorySummary.supplierCount}</span>
                                <span className="stat-label">Suppliers</span>
                            </div>
                            <div className="report-stat-card">
                                <span className="stat-value">{inventorySummary.totalSupplierEntries}</span>
                                <span className="stat-label">Price Entries</span>
                            </div>
                            <div className="report-stat-card">
                                <span className="stat-value">{multiSourceCount}</span>
                                <span className="stat-label">Multi-Source</span>
                            </div>
                        </div>

                        <div className="report-breakdown-row">
                            <div className="report-breakdown">
                                <h4>By Species</h4>
                                <div className="breakdown-bars">
                                    {inventorySummary.bySpecies.map(([species, count]) => (
                                        <div key={species} className="breakdown-item">
                                            <span className="breakdown-label">{species}</span>
                                            <div className="breakdown-bar-track">
                                                <div
                                                    className="breakdown-bar-fill"
                                                    style={{ width: `${(count / inventorySummary.totalPieces) * 100}%` }}
                                                />
                                            </div>
                                            <span className="breakdown-count">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="report-breakdown">
                                <h4>By Grade</h4>
                                <div className="breakdown-bars">
                                    {inventorySummary.byGrade.map(([grade, count]) => (
                                        <div key={grade} className="breakdown-item">
                                            <span className="breakdown-label">{grade}</span>
                                            <div className="breakdown-bar-track">
                                                <div
                                                    className="breakdown-bar-fill grade-bar"
                                                    style={{ width: `${(count / inventorySummary.totalPieces) * 100}%` }}
                                                />
                                            </div>
                                            <span className="breakdown-count">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Supplier Comparison */}
                    <section className="report-section">
                        <h3><Users size={18} /> Supplier Comparison</h3>
                        {supplierComparison.length === 0 ? (
                            <p className="report-empty">No supplier pricing data available.</p>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Supplier</th>
                                        <th>Avg $/bdft</th>
                                        <th>Pieces Quoted</th>
                                        <th>Cheapest On</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {supplierComparison.map((s, idx) => {
                                        const cheapestAvg = supplierComparison[0]?.avgCostPerBdFt || 0;
                                        const pctDiff = cheapestAvg > 0 && idx > 0
                                            ? ((s.avgCostPerBdFt - cheapestAvg) / cheapestAvg * 100)
                                            : 0;
                                        return (
                                            <tr key={s.name}>
                                                <td>
                                                    <span className="badge blue">{s.name}</span>
                                                    {idx === 0 && <span className="cheapest-tag">Cheapest</span>}
                                                </td>
                                                <td className="mono">{formatCurrency(s.avgCostPerBdFt)}</td>
                                                <td>{s.pieceCount}</td>
                                                <td>{s.lowestCount} pieces</td>
                                                <td className="pct-diff-cell">
                                                    {idx === 0 ? (
                                                        <span className="pct-badge best"><Minus size={12} /> baseline</span>
                                                    ) : (
                                                        <span className="pct-badge worse">
                                                            <TrendingUp size={12} /> +{pctDiff.toFixed(1)}%
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </section>

                    {/* Species + Grade × Supplier */}
                    {gradeBySupplier.suppliers.length > 0 && gradeBySupplier.rows.length > 0 && (
                        <section className="report-section">
                            <h3><Layers size={18} /> Avg $/bdft by Species &amp; Grade</h3>
                            <div className="report-table-scroll">
                                <table className="report-table">
                                    <thead>
                                        <tr>
                                            <th>Species / Grade / Dryness</th>
                                            {gradeBySupplier.suppliers.map(s => (
                                                <th key={s}>{s}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gradeBySupplier.rows.map(row => (
                                            <tr key={row.label}>
                                                <td><span className="badge green">{row.label}</span></td>
                                                {row.avgBySupplier.map(cell => (
                                                    <td
                                                        key={cell.supplier}
                                                        className={`mono ${cell.supplier === row.cheapest && cell.avg > 0 ? 'cheapest-cell' : ''}`}
                                                    >
                                                        {cell.avg > 0 ? formatCurrency(cell.avg) : '—'}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* Price History */}
                    <section className="report-section">
                        <h3><BarChart3 size={18} /> Price History</h3>
                        {priceHistory.bySupplier.length === 0 ? (
                            <div className="report-empty">
                                <p>No price history available yet.</p>
                                <p className="report-hint">Price history is recorded when you import supplier pricing. Future imports will show quote dates and price trends here.</p>
                            </div>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Supplier</th>
                                        <th>Last Quote</th>
                                        <th>Entries</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {priceHistory.bySupplier.map(s => (
                                        <tr key={s.name}>
                                            <td><span className="badge blue">{s.name}</span></td>
                                            <td>{s.lastDate.toLocaleDateString()}</td>
                                            <td>{s.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>

                    {/* Coverage Risk */}
                    <section className="report-section report-section-small">
                        <div className="coverage-summary">
                            <div className="coverage-stat">
                                <span className="coverage-icon warning">⚠️</span>
                                <div>
                                    <strong>{singleSourceCount}</strong> pieces with single or no supplier
                                </div>
                            </div>
                            <div className="coverage-stat">
                                <span className="coverage-icon good">✓</span>
                                <div>
                                    <strong>{multiSourceCount}</strong> pieces with 2+ suppliers
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TimberReport;
